import { Close, Refresh } from "@mui/icons-material"
import {
  Box,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material"
import { FC, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { SharedAccessPatternSaEnum } from "shared/sharedAccessPatternsData"
import { useGetThingsQuery } from "src/features/things/thingsApi"
import { useSearchParamsState } from "src/hooks/useSearchParamsState"
import { useAppSelector } from "../app/hooks"
import { selectMeIsReady } from "../features/me/meSlice"
import AdminLogComponent from "./AdminLog"
import ObjectDisplayTable from "./ObjectDisplayTable"
import SaLayout from "./SaLayout"

const cardSx = {
  bgcolor: "white",
  border: "1px solid",
  borderColor: "rgba(0, 0, 0, 0.08)",
  borderRadius: 2,
  boxShadow: "0 1px 2px rgba(35, 66, 133, 0.04)",
}

export const SaAdminLogsPage: FC = () => {
  const { t } = useTranslation()

  const isReady = useAppSelector(selectMeIsReady)
  const [status, setStatus] = useSearchParamsState("status", "")
  const qLog = useGetThingsQuery(
    {
      params: {
        "ap.name": SharedAccessPatternSaEnum.SaGetAdminLogs,
        "ap.status": status ? status : undefined,
      },
    },
    { skip: !isReady },
  )

  // osrt by createdAt desc
  const logs = useMemo(
    () =>
      [...(qLog.data?.things || [])].sort((a, b) => {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      }),
    [qLog.data],
  )

  const [selectedKeys, setSelectedKeys] = useState<string>("")
  const [selectedTitle, setSelectedTitle] = useState<string>("")

  const qItem = useGetThingsQuery(
    {
      params: {
        "ap.name": SharedAccessPatternSaEnum.GetItemByKeys,
        "ap.keys": selectedKeys,
      },
    },
    { skip: !selectedKeys },
  )
  const item = useMemo(() => qItem.data?.things[0], [qItem.data])

  return (
    <SaLayout selectedMenuItemLabelKey="adminLogs">
      <Box
        sx={{
          maxWidth: 1400,
          mx: "auto",
          px: { xs: 3, md: 5 },
          py: { xs: 4, md: 5 },
          width: "100%",
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-end"
          flexWrap="wrap"
          gap={2}
          sx={{ mb: 4 }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "primary.main",
              }}
            >
              {t("adminApp")}
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="baseline">
              <Typography
                variant="h4"
                sx={{
                  mt: 0.5,
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                  color: "text.primary",
                  lineHeight: 1.15,
                }}
              >
                {t("adminLogs")}
              </Typography>
              <Typography
                sx={{
                  color: "text.secondary",
                  fontSize: "1.1rem",
                  fontWeight: 500,
                }}
              >
                {logs?.length ?? 0}
              </Typography>
            </Stack>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {qLog.isLoading || qLog.isFetching ? (
              <CircularProgress size={22} />
            ) : (
              <IconButton
                onClick={qLog.refetch}
                size="small"
                sx={{
                  bgcolor: "rgba(35, 66, 133, 0.06)",
                  color: "primary.main",
                  "&:hover": { bgcolor: "rgba(35, 66, 133, 0.12)" },
                }}
              >
                <Refresh fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Stack>

        {/* Filter card */}
        <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, mb: 2.5 }}>
          <Stack direction="column" spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              {t("adminLogsDescription")}
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              rowGap={1}
            >
              <Typography variant="body2" sx={{ mr: 0.5 }}>
                {t("filters")}
              </Typography>
              <Chip
                label={t("all")}
                color={status ? "default" : "primary"}
                onClick={() => setStatus("")}
                size="small"
              />
              <Chip
                label={t("resolved")}
                color={status === "resolved" ? "primary" : "success"}
                onClick={() => setStatus("resolved")}
                size="small"
              />
              <Chip
                label={t("alarm")}
                color={status === "alarm" ? "primary" : "error"}
                onClick={() => setStatus("alarm")}
                size="small"
              />
            </Stack>
          </Stack>
        </Box>

        {/* Log list card */}
        <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, overflow: "hidden" }}>
          <Stack direction="column" spacing={2}>
            {logs.map((log) => (
              <AdminLogComponent
                key={log.id}
                log={log}
                onSelectKeys={(keys, title) => {
                  setSelectedKeys(keys)
                  setSelectedTitle(title)
                }}
                onResolved={qLog.refetch}
              />
            ))}
          </Stack>
        </Box>
      </Box>
      {item && selectedKeys && (
        <Drawer data-ui="UI-SL2W-202"
          anchor="right"
          open={true}
          onClose={() => setSelectedKeys("")}
          sx={{
            "& .MuiDrawer-paper": {
              width: "80vw",
              maxWidth: "1500px",
              maxHeight: "calc(100vh - 64px)",
              height: "calc(100vh - 64px)",
              paddingTop: "64px",
            },
          }}
        >
          <Stack direction="column" spacing={1} p={1}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="body1" fontWeight="bold">
                {selectedTitle}
              </Typography>
              <IconButton onClick={() => setSelectedKeys("")}>
                <Close />
              </IconButton>
            </Stack>
            <ObjectDisplayTable data={item} isLoading={qItem.isLoading} />
          </Stack>
        </Drawer>
      )}
    </SaLayout>
  )
}

export default SaAdminLogsPage
